"use client";

import Image from "next/image";
import Link from "next/link";
import { ChevronDown } from "lucide-react";
import { Locale } from "@/i18n-config";
import { getLocalizedPath } from "@/lib/slugs";
import { useEffect, useState } from "react";

import { HeaderCategory, MegaMenuItem } from "@/lib/services/services";
import MegaMenu, { type MegaMenuDict } from "@/components/layout/MegaMenu";
import LanguageSwitcher from "@/components/layout/LanguageSwitcher";

type HeaderDict = MegaMenuDict & {
    services?: string;
    about?: string;
    contact?: string;
    blog?: string;
    book_now?: string;
    [key: string]: unknown;
};

export default function Header({
    lang,
    dict,
    categories = [],
    megaMenuData = [],
    logoSrc,
}: {
    lang: Locale;
    dict: HeaderDict;
    categories?: HeaderCategory[];
    megaMenuData?: MegaMenuItem[];
    logoSrc?: string;
}) {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
    const headerLogo = logoSrc || "/cinikpoliklinik-logo.png";
    const label = (value: unknown) => String(value ?? "").trim();
    void categories;

    useEffect(() => {
        // scrollY okumasi bir sonraki kareye ertelenir ve kare basina bire
        // indirilir: ilk render'da senkron okuma "zorunlu yeniden duzenleme"
        // (forced reflow) olarak raporlaniyordu.
        let frame = 0;
        const handleScroll = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => setIsScrolled(window.scrollY > 20));
        };
        handleScroll();
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    const handleMouseEnter = () => setIsMegaMenuOpen(true);
    const handleMouseLeave = () => setIsMegaMenuOpen(false);

    return (
        <header
            className={`transform-gpu transition-all duration-300 ease-out z-40 ${isScrolled
                ? "sticky top-0 w-full border-b border-gray-200/60 bg-white/90 py-2 backdrop-blur-lg lg:left-0 lg:right-0 lg:top-4 lg:mx-auto lg:w-[95%] lg:max-w-[1300px] lg:rounded-2xl lg:border-gray-200/50 lg:shadow-xl"
                : "sticky top-0 w-full border-b border-gray-100 bg-white/95 py-2 backdrop-blur-sm lg:py-3"
                }`}
            onMouseLeave={handleMouseLeave}
        >
            <div className="container-custom flex items-center justify-between gap-3 lg:gap-6">
                <Link href={lang === "tr" ? "/" : `/${lang}`} className="flex items-center shrink-0">
                    <Image
                        src={headerLogo}
                        alt="Cinik Polikliniği"
                        width={200}
                        height={48}
                        priority
                        className={`w-auto object-contain transition-all duration-300 ${isScrolled ? "h-[30px] lg:h-[32px]" : "h-[34px] lg:h-[42px]"}`}
                    />
                </Link>

                <nav className="hidden lg:flex items-center gap-4 xl:gap-5 text-[#0f4c81] font-bold text-[15px] h-full">
                    <div
                        className="relative h-full flex items-center"
                        onMouseEnter={handleMouseEnter}
                    >
                        <Link href={getLocalizedPath("services", lang)} className="flex items-center cursor-pointer whitespace-nowrap hover:text-[#00AEEF] transition-colors group py-2">
                            <span>{label(dict.services)}</span>
                            <ChevronDown className={`w-4 h-4 ml-1 transition-transform duration-200 ${isMegaMenuOpen ? "rotate-180" : ""}`} />
                        </Link>
                    </div>

                    <Link
                        href={getLocalizedPath("about-us", lang)}
                        className="whitespace-nowrap hover:text-[#00AEEF] transition-colors"
                    >
                        {label(dict.about)}
                    </Link>

                    <Link
                        href={getLocalizedPath("contact-us", lang)}
                        className="whitespace-nowrap hover:text-[#00AEEF] transition-colors"
                    >
                        {label(dict.contact)}
                    </Link>

                    <Link
                        href={getLocalizedPath("blog", lang)}
                        className="whitespace-nowrap hover:text-[#00AEEF] transition-colors"
                    >
                        {label(dict.blog)}
                    </Link>
                </nav>

                <div className="shrink-0 flex items-center gap-2 text-[#0f4c81] lg:gap-4">
                    <LanguageSwitcher currentLang={lang} />

                    <Link
                        href={getLocalizedPath("book-now", lang)}
                        className="hidden items-center rounded-[4px] bg-[#0f4c81] px-4 py-2 text-[13px] font-semibold text-white shadow-sm transition-all hover:scale-105 hover:bg-[#0a355c] sm:flex"
                    >
                        <span className="relative flex h-2 w-2 mr-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                        </span>
                        {label(dict.book_now)}
                    </Link>
                </div>
            </div>

            <MegaMenu
                isOpen={isMegaMenuOpen}
                items={megaMenuData}
                onClose={() => setIsMegaMenuOpen(false)}
                dict={dict}
            />
        </header>
    );
}
